import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { AuthForm, authFormSchema } from "../../models/Form";
import { auth, db } from "../../firebase";
import { doc, setDoc } from "firebase/firestore";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHook";
import { useEffect, useState } from "react";

import ResetPassword from "../../components/ResetPassword/ResetPassword";
import { authClasses } from "./authClasses";
import { login } from "../../features/authSlice";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

const getAuthErrorMessage = (code?: string) => {
  switch (code) {
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "The email or password does not match our records.";
    case "auth/invalid-email":
      return "Enter a valid email address.";
    case "auth/email-already-in-use":
      return "That email already has an account. Try signing in instead.";
    case "auth/user-not-found":
      return "No account was found for that email address.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait a few minutes and try again.";
    case "auth/weak-password":
      return "Password must be at least 6 characters.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    case "auth/operation-not-allowed":
      return "Email sign in is not available right now.";
    default:
      return "We could not complete that request. Please try again.";
  }
};

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const Auth = () => {
  const [authType, setAuthType] = useState<"login" | "sign-up">("login");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [resetPassword, setResetPassword] = useState(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState("");
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState<
    string | null
  >(null);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(
    null
  );

  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const dashboardPath = `/${location.search}`;

  useEffect(() => {
    if (Boolean(user)) {
      navigate(dashboardPath);
    }
  }, [user, navigate, dashboardPath]);

  const {
    container,
    authShell,
    authIntro,
    authCard,
    authCardHeader,
    form,
    button,
    input,
    text,
    textLight,
    link,
    hr,
    forgotPasswordButton,
  } = authClasses;

  const handlePasswordReset = async () => {
    const email = resetPasswordEmail.trim().toLowerCase();
    if (!email.length) {
      setResetPasswordError("Email is required.");
      setResetPasswordSuccess(null);
      return;
    }
    if (!isValidEmail(email)) {
      setResetPasswordError("Enter a valid email address.");
      setResetPasswordSuccess(null);
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetPasswordSuccess(
        "Password reset email sent. Please check your inbox."
      );
      setResetPasswordError(null);
    } catch (error: any) {
      setResetPasswordError(getAuthErrorMessage(error.code));
      setResetPasswordSuccess(null);
    }
  };

  const openResetPassword = () => {
    setResetPasswordEmail("");
    setResetPasswordSuccess(null);
    setResetPasswordError(null);
    setResetPassword(true);
  };

  const closeResetPassword = () => {
    setResetPassword(false);
    setResetPasswordEmail("");
    setResetPasswordSuccess(null);
    setResetPasswordError(null);
  };

  const handleFormSubmit = async (data: AuthForm) => {
    setErrorMessage(null);
    setLoading(true);
    const email = data.email.trim().toLowerCase();
    const { password, confirmPassword } = data;
    const firstName = data.firstName?.trim() ?? "";
    const lastName = data.lastName?.trim() ?? "";

    if (!isValidEmail(email)) {
      setErrorMessage("Enter a valid email address.");
      setLoading(false);
      return;
    }

    if (authType === "sign-up") {
      if (!firstName || !lastName) {
        setErrorMessage("Enter your first and last name.");
        setLoading(false);
        return;
      }
      if (password !== confirmPassword) {
        setErrorMessage("Passwords do not match.");
        setLoading(false);
        return;
      }
      try {
        const { user } = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );

        await setDoc(doc(db, "users", user.uid), {
          email,
          firstName,
          lastName,
        });
        setLoading(false);

        if (user && user.email)
          dispatch(
            login({
              email: user.email,
              id: user.uid,
            })
          );
      } catch (error: any) {
        setLoading(false);
        setErrorMessage(getAuthErrorMessage(error.code));
      }
    } else {
      try {
        const { user } = await signInWithEmailAndPassword(
          auth,
          email,
          password
        );
        setLoading(false);
        if (user && user.email)
          dispatch(
            login({
              email: user.email,
              id: user.uid,
            })
          );
      } catch (error: any) {
        setErrorMessage(getAuthErrorMessage(error.code));
        setLoading(false);
      }
    }
  };

  const handleAuthType = () => {
    setErrorMessage(null);
    setAuthType((prevAuthType) =>
      prevAuthType === "login" ? "sign-up" : "login"
    );
  };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AuthForm>({
    resolver: yupResolver(authFormSchema),
    mode: "onChange",
  });

  const watchedEmail = watch("email") ?? "";
  const watchedPassword = watch("password") ?? "";
  const watchedConfirmPassword = watch("confirmPassword") ?? "";
  const watchedFirstName = watch("firstName") ?? "";
  const watchedLastName = watch("lastName") ?? "";
  const passwordsMatch =
    authType === "login" || watchedPassword === watchedConfirmPassword;
  const canSubmit =
    isValidEmail(watchedEmail) &&
    watchedPassword.length >= 6 &&
    (authType === "login" ||
      (watchedFirstName.trim().length > 0 && watchedLastName.trim().length > 0)) &&
    passwordsMatch &&
    !loading;

  return (
    <>
      <ResetPassword
        resetPasswordEmail={resetPasswordEmail}
        resetPasswordSuccess={resetPasswordSuccess}
        resetPasswordError={resetPasswordError}
        setResetPasswordEmail={setResetPasswordEmail}
        isOpen={resetPassword}
        onClose={closeResetPassword}
        handlePasswordReset={handlePasswordReset}
      />
      <div className={container}>
        <div className={authShell}>
          <section className={authIntro}>
            <div className="mb-5 inline-flex rounded-full border border-[rgba(164,67,51,0.4)] bg-[rgba(164,67,51,0.15)] px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-accent-color-2">
              Sidewinder Volleyball
            </div>
            <h1 className="mb-4 text-4xl leading-tight text-white md:text-5xl">
              Sidewinder Registration Portal
            </h1>
            <p className="max-w-xl text-base leading-7 text-slate-300">
              Register for Sidewinder clinics, tryouts, leagues, and team
              events.
            </p>
            <div className="mt-7 grid gap-3 text-sm text-slate-300">
              <div className="rounded-md border border-white/10 bg-black/20 p-4">
                <strong className="block text-accent-color-2">
                  Youth athletes use a parent account
                </strong>
                <span>
                  Parents or guardians should manage youth athlete
                  registrations.
                </span>
              </div>
              <div className="rounded-md border border-white/10 bg-black/20 p-4">
                <strong className="block text-accent-color-2">
                  Built for more Sidewinder programs
                </strong>
                <span>
                  Adult leagues and Hatchlings registration will live here too.
                </span>
              </div>
            </div>
          </section>

          <section className={authCard}>
            <div className={authCardHeader}>
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-accent-color-2">
                {authType === "login" ? "Welcome back" : "Create account"}
              </span>
              <h2 className="m-0 text-2xl text-white">
                {authType === "login" ? "Sign in" : "Sign up"}
              </h2>
              <p className={`${text} m-0`}>
                {authType === "login"
                  ? "Access your registrations."
                  : "Create your account with your own name and email."}
              </p>
            </div>

            <form onSubmit={handleSubmit(handleFormSubmit)} className={form}>
              {errorMessage && (
                <p className="m-0 rounded-md border border-red-300/30 bg-red-500/15 px-3 py-2 text-sm text-red-100">
                  {errorMessage}
                </p>
              )}
              <div className="grid gap-y-4">
                {authType === "sign-up" ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="grid gap-2">
                      <span className={textLight}>First name</span>
                      <input
                        type="text"
                        placeholder="First name"
                        className={input}
                        {...register("firstName")}
                      />
                    </label>
                    <label className="grid gap-2">
                      <span className={textLight}>Last name</span>
                      <input
                        type="text"
                        placeholder="Last name"
                        className={input}
                        {...register("lastName")}
                      />
                    </label>
                  </div>
                ) : null}

                <label className="grid gap-2">
                  <span className={textLight}>Email</span>
                  <input
                    type="email"
                    placeholder="parent@example.com"
                    className={input}
                    {...register("email")}
                  />
                  {errors.email ? (
                    <span className="text-sm text-red-200">
                      {errors.email.message}
                    </span>
                  ) : null}
                </label>

                <label className="grid gap-2">
                  <span className={textLight}>Password</span>
                  <input
                    {...register("password")}
                    type="password"
                    placeholder={
                      authType === "login" ? "*******" : "Create a Password"
                    }
                    className={input}
                  />
                  {errors.password ? (
                    <span className="text-sm text-red-200">
                      {errors.password.message}
                    </span>
                  ) : null}
                </label>

                {authType === "sign-up" ? (
                  <label className="grid gap-2">
                    <span className={textLight}>Confirm Password</span>
                    <input
                      {...register("confirmPassword")}
                      type="password"
                      placeholder="Confirm Password"
                      className={input}
                    />
                    {watchedConfirmPassword && !passwordsMatch ? (
                      <span className="text-sm text-red-200">
                        Passwords do not match.
                      </span>
                    ) : null}
                  </label>
                ) : null}

                <button disabled={!canSubmit} className={button}>
                  {loading
                    ? authType === "login"
                      ? "Signing in..."
                      : "Signing up..."
                    : `Sign ${authType === "login" ? "in" : "up"} with Email`}
                </button>
              </div>

              <div className="text-sm font-light text-slate-300">
                {authType === "login" ? (
                  <span>
                    Don&apos;t have an account yet?{" "}
                    <button
                      type="button"
                      onClick={handleAuthType}
                      className={link}
                    >
                      Sign up
                    </button>
                  </span>
                ) : (
                  <span>
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={handleAuthType}
                      className={link}
                    >
                      Sign in
                    </button>
                  </span>
                )}
              </div>
              {authType === "login" ? (
                <div className="flex items-center">
                  <hr className={hr} />
                  <button
                    onClick={openResetPassword}
                    type="button"
                    className={forgotPasswordButton}
                  >
                    Forgot password
                  </button>
                  <hr className={hr} />
                </div>
              ) : null}
            </form>
          </section>
        </div>
      </div>
    </>
  );
};

export default Auth;
